import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getHistoriaClinicaPorCedula, createHistoriaClinica, getValoresCampo, actualizarValoresCampo,
  agregarDiagnostico, crearReceta, cargarArchivoCampo
} from '../../api/historiaClinicaService';
import { generarPDFReceta } from '../../utils/pdfGenerator';
import { generarYDescargarHistoriaClinica } from '../../utils/pdfLibHistoriaClinica';
import { logAxiosError, validarRecetaMedica } from '../../utils/debugHelpers';
import { cargarRecetas as cargarRecetasUtil } from '../../utils/recetaHelpers';
import { getPlantilla } from '../../api/plantillaService';
import { AuthContext } from '../../context/AuthContext';
import { API_ROOT_URL } from '../../config/api';

// Componente para visualizar una receta existente
const VisualizarReceta = ({ receta, paciente }) => {
  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return 'Fecha no disponible';
    const fechaObj = new Date(fecha);
    return fechaObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div style={{
      padding: '20px',
      backgroundColor: 'white'
    }}>
      {/* Cabecera de la receta */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottom: '1px solid #edf2f7',
        paddingBottom: '16px',
        marginBottom: '20px'
      }}>
        <div>
          <h4 style={{
            margin: '0 0 6px 0',
            fontSize: '20px',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: '600'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#40c8e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-prescription-bottle-alt" style={{ color: 'white' }}></i>
            </div>
            Receta Médica
          </h4>
          <div style={{ fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="far fa-calendar-alt"></i>
            <span>Fecha: {formatFecha(receta.fecha || receta.createdAt)}</span>
          </div>
        </div>
        {receta.diagnostico && (
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#0369a1',
            border: '1px solid #bae6fd',
            marginLeft: '10px',
            maxWidth: '300px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '3px', fontSize: '13px' }}>
              Diagnóstico asociado:
            </div>
            <div>
              {typeof receta.diagnostico === 'string' ? receta.diagnostico : receta.diagnostico.descripcion}
            </div>
          </div>
        )}
      </div>

      {/* Indicaciones generales */}
      {receta.indicaciones && (
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{
            margin: '0 0 10px 0',
            fontSize: '16px',
            color: '#333',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fas fa-clipboard-list" style={{ color: '#40c8e0' }}></i>
            Indicaciones Generales
          </h5>
          <div style={{
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            fontSize: '14px',
            border: '1px solid #edf2f7',
            lineHeight: '1.6',
            color: '#444'
          }}>
            {receta.indicaciones}
          </div>
        </div>
      )}

      {/* Lista de medicamentos */}
      <div>
        <h5 style={{
          margin: '0 0 15px 0',
          fontSize: '16px',
          color: '#333',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className="fas fa-pills" style={{ color: '#40c8e0' }}></i>
          Medicamentos
        </h5>
        {receta.medicamentos && receta.medicamentos.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {receta.medicamentos.map((med, idx) => (
              <div key={idx} style={{
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '10px',
                border: '1px solid #edf2f7',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                ':hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid #edf2f7',
                  paddingBottom: '12px',
                  marginBottom: '12px'
                }}>
                  <h6 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {med.nombre}
                  </h6>
                  <span style={{
                    backgroundColor: '#e0f2fe',
                    padding: '5px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    color: '#0284c7',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <i className="fas fa-capsules"></i>
                    Medicamento #{idx + 1}
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  {med.dosis && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #f3f4f6'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        marginBottom: '5px',
                        fontWeight: '500'
                      }}>
                        <i className="fas fa-prescription"></i> Dosis:
                      </div>
                      <div style={{ fontSize: '15px', color: '#333' }}>{med.dosis}</div>
                    </div>
                  )}

                  {med.frecuencia && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #f3f4f6'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        marginBottom: '5px',
                        fontWeight: '500'
                      }}>
                        <i className="fas fa-clock"></i> Frecuencia:
                      </div>
                      <div style={{ fontSize: '15px', color: '#333' }}>{med.frecuencia}</div>
                    </div>
                  )}

                  {med.duracion && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #f3f4f6'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        marginBottom: '5px',
                        fontWeight: '500'
                      }}>
                        <i className="fas fa-calendar-day"></i> Duración:
                      </div>
                      <div style={{ fontSize: '15px', color: '#333' }}>{med.duracion}</div>
                    </div>
                  )}
                </div>

                {med.indicaciones && (
                  <div style={{
                    marginTop: '5px',
                    padding: '14px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #e0f2fe'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      color: '#0369a1',
                      fontWeight: '500',
                      marginBottom: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <i className="fas fa-info-circle"></i> Indicaciones específicas:
                    </div>
                    <div style={{ margin: 0, fontSize: '14px', color: '#444', lineHeight: '1.5' }}>
                      {med.indicaciones}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px dashed #e2e8f0'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: '#e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 10px',
              color: '#94a3b8'
            }}>
              <i className="fas fa-pills" style={{ fontSize: '20px' }}></i>
            </div>
            <div style={{ fontWeight: '500', marginBottom: '5px' }}>
              No hay medicamentos registrados
            </div>
            <div style={{ fontSize: '14px' }}>
              Esta receta no contiene medicamentos específicos
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const HistoriaClinicaDetalle = () => {
  const { cedula } = useParams();
  const { currentUser } = useContext(AuthContext);

  const [historia, setHistoria] = useState(null);
  const [paciente, setPaciente] = useState(null);
  const [plantilla, setPlantilla] = useState(null);
  const [valoresCampo, setValoresCampo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sinHistoria, setSinHistoria] = useState(false);
  const [creandoHistoria, setCreandoHistoria] = useState(false);
  const [pestanaActiva, setPestanaActiva] = useState('datos');
  const [modoEdicion, setModoEdicion] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [diagnosticos, setDiagnosticos] = useState([]);
  const [recetas, setRecetas] = useState([]);

  // Estado para manejar modal de imagen
  const [imagenSeleccionada, setImagenSeleccionada] = useState(null);
  const [mostrarImagenCompleta, setMostrarImagenCompleta] = useState(false);

  // Modales y formularios
  const [mostrarModalDiagnostico, setMostrarModalDiagnostico] = useState(false);
  const [mostrarModalReceta, setMostrarModalReceta] = useState(false);
  const [formDiagnostico, setFormDiagnostico] = useState({
    descripcion: '',
    codigoCIE10: '',
    detalles: ''
  });
  const [formReceta, setFormReceta] = useState({
    diagnosticoId: '',
    indicaciones: '',
    medicamentos: [{ nombre: '', dosis: '', frecuencia: '', duracion: '', indicaciones: '' }]
  });
  const [diagnosticoSeleccionado, setDiagnosticoSeleccionado] = useState(null);

  useEffect(() => {
    const cargarHistoriaClinica = async () => {
      try {
        setIsLoading(true);
        const responseHistoria = await getHistoriaClinicaPorCedula(cedula);

        if (responseHistoria.data && responseHistoria.data.data) {
          const historiaData = responseHistoria.data.data;
          setHistoria(historiaData);
          setPaciente(historiaData.paciente);
          setSinHistoria(false);

          if (historiaData.plantillaId) {
            const responsePlantilla = await getPlantilla(historiaData.plantillaId);
            if (responsePlantilla.data && responsePlantilla.data.data) {
              setPlantilla(responsePlantilla.data.data);
            }
          }

          const responseValores = await getValoresCampo(historiaData.id);
          if (responseValores.data && responseValores.data.data) {
            const historiaConValores = responseValores.data.data;
            if (historiaConValores.valores) {
              const valoresObj = {};
              historiaConValores.valores.forEach(valor => {
                valoresObj[valor.campoId] = valor.valor;
              });
              setValoresCampo(valoresObj);
            } else if (historiaConValores.valoresPorCampo) {
              setValoresCampo(historiaConValores.valoresPorCampo);
            }
          }

          if (historiaData.diagnosticos) {
            setDiagnosticos(historiaData.diagnosticos);
          }

          if (historiaData.recetas && historiaData.recetas.length > 0) {
            setRecetas(historiaData.recetas);
          } else if (historiaData.diagnosticos) {
            const recetasEnDiagnosticos = [];
            historiaData.diagnosticos.forEach(diagnostico => {
              if (diagnostico.recetas && diagnostico.recetas.length > 0) {
                recetasEnDiagnosticos.push(...diagnostico.recetas);
              }
            });

            if (recetasEnDiagnosticos.length > 0) {
              setRecetas(recetasEnDiagnosticos);
            }
          }
        } else {
          setSinHistoria(true);
        }

        setError(null);
      } catch (err) {
        if (err?.response?.status === 404) {
          setHistoria(null);
          setPaciente(null);
          setPlantilla(null);
          setValoresCampo({});
          setDiagnosticos([]);
          setRecetas([]);
          setSinHistoria(true);
          setError(null);
        } else {
          logAxiosError('Error al cargar historia clínica:', err);
          setError('No se pudo cargar la historia clínica. Por favor, intente de nuevo más tarde.');
        }

      } finally {
        setIsLoading(false);
      }
    };

    cargarHistoriaClinica();
  }, [cedula]);

  const crearHistoriaAutomatica = async () => {
    try {
      setCreandoHistoria(true);
      await createHistoriaClinica({ cedula });
      const responseHistoria = await getHistoriaClinicaPorCedula(cedula);

      if (responseHistoria?.data?.data) {
        const historiaData = responseHistoria.data.data;
        setHistoria(historiaData);
        setPaciente(historiaData.paciente);
        setSinHistoria(false);
        setError(null);

        if (historiaData.plantillaId) {
          const responsePlantilla = await getPlantilla(historiaData.plantillaId);
          if (responsePlantilla.data && responsePlantilla.data.data) {
            setPlantilla(responsePlantilla.data.data);
          }
        }

        const responseValores = await getValoresCampo(historiaData.id);
        if (responseValores.data && responseValores.data.data) {
          const historiaConValores = responseValores.data.data;
          if (historiaConValores.valores) {
            const valoresObj = {};
            historiaConValores.valores.forEach(valor => {
              valoresObj[valor.campoId] = valor.valor;
            });
            setValoresCampo(valoresObj);
          } else if (historiaConValores.valoresPorCampo) {
            setValoresCampo(historiaConValores.valoresPorCampo);
          }
        }

        if (historiaData.diagnosticos) {
          setDiagnosticos(historiaData.diagnosticos);
        }

        if (historiaData.recetas && historiaData.recetas.length > 0) {
          setRecetas(historiaData.recetas);
        } else if (historiaData.diagnosticos) {
          const recetasEnDiagnosticos = [];
          historiaData.diagnosticos.forEach(diagnostico => {
            if (diagnostico.recetas && diagnostico.recetas.length > 0) {
              recetasEnDiagnosticos.push(...diagnostico.recetas);
            }
          });

          setRecetas(recetasEnDiagnosticos);
        }
      }
    } catch (err) {
      logAxiosError('Error al crear historia clínica automáticamente:', err);
      alert(err?.response?.data?.message || 'No se pudo crear la historia clínica automáticamente.');
    } finally {
      setCreandoHistoria(false);
    }
  };

  // Función para guardar la receta
  const guardarReceta = async (e) => {
    e.preventDefault();
    if (!formReceta.diagnosticoId) {
      alert('Debe elegir un diagnóstico para la receta');
      return;
    }

    // Validar la receta médica antes de guardar
    const erroresValidacion = validarRecetaMedica(formReceta);
    if (erroresValidacion.length > 0) {
      alert(`Errores en la receta: ${erroresValidacion.join('\n')}`);
      return;
    }

    try {
      const res = await crearReceta(historia.id, formReceta.diagnosticoId, formReceta);
      if (res.data && res.data.data) {
        // Refrescar lista de recetas desde el backend
        await cargarRecetasUtil(historia.id, getValoresCampo, setRecetas);
        // O alternativamente:
        // setRecetas(prev => [...prev, res.data.data]);
        setFormReceta({
          diagnosticoId: diagnosticos[0]?.id || '',
          indicaciones: '',
          medicamentos: [{ nombre: '', dosis: '', frecuencia: '', duracion: '', indicaciones: '' }]
        });
        setMostrarModalReceta(false);
      }
    } catch (err) {
      console.error('Error al crear receta:', err);
      alert('No se pudo guardar la receta. Intenta otra vez.');
    }
  };
  // Manejar cambios en campos de la historia clínica
  const handleCampoChange = (campoId, valor) => {
    setValoresCampo(prevValores => ({
      ...prevValores,
      [campoId]: valor
    }));
  };

  // Guardar cambios en la historia clínica
  const guardarCambios = async (auto = false, valores = valoresCampo) => {
    try {
      setGuardando(true);

      const valoresArray = Object.entries(valores).map(([campoId, valor]) => ({
        campoId,
        valor: valor?.toString() || ''
      }));

      const response = await actualizarValoresCampo(historia.id, { valores: valoresArray });

      // Utilizamos la respuesta para verificar el estado de la operación
      if (response.data && response.data.status === 'success') {
        console.log('Valores actualizados correctamente:', response.data);
      }

      try {
        const responseActualizada = await getValoresCampo(historia.id);

        if (responseActualizada.data && responseActualizada.data.data) {
          const historiaActualizada = responseActualizada.data.data;

          if (historiaActualizada.valoresPorCampo) {
            setValoresCampo(historiaActualizada.valoresPorCampo);
          } else if (historiaActualizada.valores) {
            const valoresActualizados = {};
            historiaActualizada.valores.forEach(valor => {
              valoresActualizados[valor.campoId] = valor.valor;
            });
            setValoresCampo(valoresActualizados);
          }
        }
      } catch (errorRecarga) {
        console.error('Error al recargar datos después de guardar:', errorRecarga);
      }

      setModoEdicion(false);
      alert('Historia clínica actualizada correctamente');
    } catch (error) {
      console.error('Error al guardar cambios:', error);
      if (!auto) alert('Error al guardar los cambios. Intente nuevamente.');
    } finally {
      setGuardando(false);
      // solo salir de edición si fue un guardado manual
      if (!auto) {
        setModoEdicion(false);
        alert('Historia clínica actualizada correctamente');
      }
    }
  };

  // Manejar carga de archivos
  const handleFileUpload = async (campoId, file) => {
    try {
      if (!file) return;
      const response = await cargarArchivoCampo(historia.id, campoId, file);
  
      if (response.data?.data) {
        const fileUrl = response.data.data.url;
        // Construye el nuevo objeto de valores
        const nuevosValores = { 
          ...valoresCampo, 
          [campoId]: fileUrl 
        };
        // Actualiza el state con la URL nueva
        setValoresCampo(nuevosValores);

      } else {
        alert('Error al procesar la respuesta del servidor.');
      }
    } catch (error) {
      console.error('Error al cargar archivo:', error);
      alert('Error al cargar el archivo. Intente nuevamente.');
    }
  };
  

  // Manejar cambios en el formulario de diagnóstico
  const handleDiagnosticoChange = (e) => {
    const { name, value } = e.target;
    setFormDiagnostico({
      ...formDiagnostico,
      [name]: value
    });
  };

  // Guardar nuevo diagnóstico
  const guardarDiagnostico = async (e) => {
    e.preventDefault();

    if (!formDiagnostico.descripcion) {
      alert('La descripción del diagnóstico es obligatoria');
      return;
    }

    // Usar la función handleDiagnosticoChange para validar antes de guardar
    if (!formDiagnostico.fecha) {
      const fechaActual = new Date().toISOString().split('T')[0];
      const event = { target: { name: 'fecha', value: fechaActual } };
      handleDiagnosticoChange(event);
    }

    try {
      const response = await agregarDiagnostico(historia.id, formDiagnostico);

      if (response.data && response.data.data) {
        setDiagnosticos(prevDiagnosticos => [...prevDiagnosticos, response.data.data]);
        await cargarRecetasUtil(historia.id, getValoresCampo, setRecetas);

        setFormDiagnostico({
          descripcion: '',
          codigoCIE10: '',
          detalles: ''
        });
        setMostrarModalDiagnostico(false);
      }
    } catch (error) {
      console.error('Error al guardar diagnóstico:', error);
      alert('Error al guardar el diagnóstico. Intente nuevamente.');
    }
  };

  // Otras funciones auxiliares
  const formatoFecha = (fecha) => {
    if (!fecha) return 'Fecha no disponible';
    const fechaObj = new Date(fecha);
    return fechaObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatearUrlImagen = (url) => {
    if (!url) return '';

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    const baseUrl = API_ROOT_URL || '';
    const relativePath = url.startsWith('/') ? url : `/${url}`;

    return baseUrl ? `${baseUrl}${relativePath}` : relativePath;
  };

  const abrirImagenCompleta = (url) => {
    const urlFormateada = formatearUrlImagen(url);
    setImagenSeleccionada(urlFormateada);
    setMostrarImagenCompleta(true);
  };

  // Estilos para inputs y labels
  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#555'
  };

  const inputStyle = {
    width: '100%',
    maxWidth: '100%',
    padding: '14px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '15px',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const readOnlyStyle = {
    padding: '12px',
    backgroundColor: '#f9f9ff',
    borderRadius: '6px',
    border: '1px solid #eaeaea',
    fontSize: '14px',
    color: '#444'
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236a5acd\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
    paddingRight: '40px'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical'
  };

  const buttonPrimaryStyle = {
    padding: '12px 20px',
    backgroundColor: '#6a5acd',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 4px rgba(106, 90, 205, 0.3)'
  };

  const buttonSecondaryStyle = {
    padding: '12px 20px',
    backgroundColor: '#f5f5f5',
    color: '#555',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  };

  // Renderizar campo según su tipo
  const renderizarCampo = (campo, seccion) => {
    const valor = valoresCampo[campo.id] || '';
    const esEditable = modoEdicion;

    switch (campo.tipo) {
      case 'TEXTO_CORTO':
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={valor}
                  onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                  style={inputStyle}
                  required={campo.esRequerido}
                  placeholder={`Ingrese ${campo.nombre.toLowerCase()}`}
                />
              </div>
            ) : (
              <div style={readOnlyStyle}>
                {valor || 'No registrado'}
              </div>
            )}
          </div>
        );

      case 'TEXTO_LARGO':
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <textarea
                value={valor}
                onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                style={textareaStyle}
                required={campo.esRequerido}
                placeholder={`Ingrese ${campo.nombre.toLowerCase()}`}
              />
            ) : (
              <div style={{
                ...readOnlyStyle,
                whiteSpace: 'pre-wrap',
                minHeight: '80px'
              }}>
                {valor || 'No registrado'}
              </div>
            )}
          </div>
        );

      case 'NUMERO':
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  value={valor}
                  onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                  style={inputStyle}
                  required={campo.esRequerido}
                  placeholder={`Ingrese valor numérico`}
                />
              </div>
            ) : (
              <div style={readOnlyStyle}>
                {valor || 'No registrado'}
              </div>
            )}
          </div>
        );

      case 'FECHA':
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <div style={{ position: 'relative' }}>
                <input
                  type="date"
                  value={valor}
                  onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                  style={inputStyle}
                  required={campo.esRequerido}
                />
              </div>
            ) : (
              <div style={readOnlyStyle}>
                {valor ? formatoFecha(valor) : 'No registrado'}
              </div>
            )}
          </div>
        );

      case 'SELECCION_SIMPLE':
        const opciones = campo.opciones ? JSON.parse(campo.opciones) : [];
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <select
                value={valor}
                onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                style={selectStyle}
                required={campo.esRequerido}
              >
                <option value="">-- Seleccionar --</option>
                {opciones.map((opcion, index) => (
                  <option key={index} value={opcion}>
                    {opcion}
                  </option>
                ))}
              </select>
            ) : (
              <div style={readOnlyStyle}>
                {valor || 'No seleccionado'}
              </div>
            )}
          </div>
        );

      case 'SELECCION_MULTIPLE':
        const opcionesMultiple = campo.opciones ? JSON.parse(campo.opciones) : [];
        const valoresSeleccionados = valor ? valor.split(',') : [];

        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '6px',
                padding: '10px 15px',
                maxHeight: '150px',
                overflowY: 'auto',
                backgroundColor: '#fff',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)'
              }}>
                {opcionesMultiple.map((opcion, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}>
                      <div style={{
                        position: 'relative',
                        width: '18px',
                        height: '18px',
                        marginRight: '10px'
                      }}>
                        <input
                          type="checkbox"
                          checked={valoresSeleccionados.includes(opcion)}
                          onChange={(e) => {
                            const newValues = e.target.checked
                              ? [...valoresSeleccionados, opcion]
                              : valoresSeleccionados.filter(val => val !== opcion);
                            handleCampoChange(campo.id, newValues.join(','));
                          }}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{
                          position: 'absolute',
                          width: '18px',
                          height: '18px',
                          backgroundColor: valoresSeleccionados.includes(opcion) ? '#6a5acd' : '#fff',
                          border: valoresSeleccionados.includes(opcion) ? '1px solid #6a5acd' : '1px solid #ddd',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          transition: 'all 0.2s ease'
                        }}>
                          {valoresSeleccionados.includes(opcion) && (
                            <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                          )}
                        </span>
                      </div>
                      {opcion}
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <div style={readOnlyStyle}>
                {valoresSeleccionados.length > 0
                  ? valoresSeleccionados.join(', ')
                  : 'Ninguno seleccionado'}
              </div>
            )}
          </div>
        );

      case 'BOOLEANO':
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  <div style={{
                    position: 'relative',
                    width: '18px',
                    height: '18px',
                    marginRight: '10px'
                  }}>
                    <input
                      type="radio"
                      name={`campo_${campo.id}`}
                      value="true"
                      checked={valor === 'true'}
                      onChange={() => handleCampoChange(campo.id, 'true')}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      position: 'absolute',
                      width: '18px',
                      height: '18px',
                      backgroundColor: valor === 'true' ? '#6a5acd' : '#fff',
                      border: valor === 'true' ? '1px solid #6a5acd' : '1px solid #ddd',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      {valor === 'true' && (
                        <span style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '50%'
                        }}></span>
                      )}
                    </span>
                  </div>
                  Sí
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  <div style={{
                    position: 'relative',
                    width: '18px',
                    height: '18px',
                    marginRight: '10px'
                  }}>
                    <input
                      type="radio"
                      name={`campo_${campo.id}`}
                      value="false"
                      checked={valor === 'false'}
                      onChange={() => handleCampoChange(campo.id, 'false')}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      position: 'absolute',
                      width: '18px',
                      height: '18px',
                      backgroundColor: valor === 'false' ? '#6a5acd' : '#fff',
                      border: valor === 'false' ? '1px solid #6a5acd' : '1px solid #ddd',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      {valor === 'false' && (
                        <span style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '50%'
                        }}></span>
                      )}
                    </span>
                  </div>
                  No
                </label>
              </div>
            ) : (
              <div style={readOnlyStyle}>
                {valor === 'true' ? 'Sí' : valor === 'false' ? 'No' : 'No registrado'}
              </div>
            )}
          </div>
        );

      case 'IMAGEN':
      case 'ARCHIVO':
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            {esEditable ? (
              <div>
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  padding: '25px 20px',
                  marginBottom: '15px',
                  backgroundColor: '#f8f9ff',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#6a5acd',
                    backgroundColor: '#f0f3ff'
                  }
                }}>
                  <input
                    type="file"
                    id={`file-upload-${campo.id}`}
                    
                    onChange={(e) => handleFileUpload(campo.id, e.target.files[0])}
                    style={{ display: 'none' }}
                    accept={campo.tipo === 'IMAGEN' ? 'image/*' : undefined}
                  />
                  <label
                    htmlFor={`file-upload-${campo.id}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      backgroundColor: '#f0f3fb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px',
                      color: '#6a5acd',
                      fontSize: '24px'
                    }}>
                      <i className={campo.tipo === 'IMAGEN' ? 'fas fa-image' : 'fas fa-file-upload'}></i>
                    </div>

                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#6a5acd' }}>
                      {campo.tipo === 'IMAGEN' ? 'Subir imagen' : 'Subir archivo'}
                    </div>
                    <p style={{ margin: '0', color: '#888', fontSize: '14px' }}>
                      {campo.tipo === 'IMAGEN'
                        ? 'Formatos aceptados: JPG, PNG, GIF'
                        : 'Formatos aceptados: PDF, DOC, DOCX'}
                    </p>
                  </label>
                </div>

                {valor && (
                  <div style={{ marginTop: '15px' }}>
                    {campo.tipo === 'IMAGEN' ? (
                      <div style={{
                        border: '1px solid #eaeaea',
                        padding: '15px',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                          <img
                            src={formatearUrlImagen(valor)}
                            alt={campo.nombre}
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              maxHeight: '300px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => abrirImagenCompleta(valor)}
                            onError={(e) => {
                              console.error('Error al cargar imagen:', valor);
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/300x200?text=Error+al+cargar+imagen';
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => abrirImagenCompleta(valor)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#6a5acd',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <i className="fas fa-search-plus"></i>
                            Ver imagen
                          </button>
                          <a
                            href={formatearUrlImagen(valor)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#40c8e0',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <i className="fas fa-download"></i>
                            Descargar
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        border: '1px solid #eaeaea',
                        padding: '15px',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#f0f3fb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '15px',
                            color: '#6a5acd',
                            fontSize: '18px'
                          }}>
                            <i className="fas fa-file-alt"></i>
                          </div>
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#444' }}>Archivo adjunto</div>
                            <div style={{ color: '#666', fontSize: '13px' }}>
                              {valor.split('/').pop()}
                            </div>
                          </div>
                        </div>
                        <a
                          href={formatearUrlImagen(valor)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#6a5acd',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px'
                          }}
                        >
                          <i className="fas fa-external-link-alt"></i>
                          Ver archivo
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                {valor ? (
                  campo.tipo === 'IMAGEN' ? (
                    <div style={{
                      border: '1px solid #eaeaea',
                      padding: '15px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                        <img
                          src={formatearUrlImagen(valor)}
                          alt={campo.nombre}
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '300px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                          onClick={() => abrirImagenCompleta(valor)}
                          onError={(e) => {
                            console.error('Error al cargar imagen:', valor);
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/300x200?text=Error+al+cargar+imagen';
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={() => abrirImagenCompleta(valor)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#6a5acd',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <i className="fas fa-search-plus"></i>
                          Ver imagen
                        </button>
                        <a
                          href={formatearUrlImagen(valor)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#40c8e0',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <i className="fas fa-download"></i>
                          Descargar
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      border: '1px solid #eaeaea',
                      padding: '15px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#f0f3fb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '15px',
                          color: '#6a5acd',
                          fontSize: '18px'
                        }}>
                          <i className="fas fa-file-alt"></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#444' }}>Archivo adjunto</div>
                          <div style={{ color: '#666', fontSize: '13px' }}>
                            {valor.split('/').pop()}
                          </div>
                        </div>
                      </div>
                      <a
                        href={formatearUrlImagen(valor)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#6a5acd',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <i className="fas fa-external-link-alt"></i>
                        Ver archivo
                      </a>
                    </div>
                  )
                ) : (
                  <div style={{
                    padding: '25px',
                    backgroundColor: '#f8f9ff',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px dashed #e0e4f1'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: '#f0f3fb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 10px',
                      color: '#d1d5db',
                      fontSize: '24px'
                    }}>
                      <i className={`fas fa-${campo.tipo === 'IMAGEN' ? 'image' : 'file'}`}></i>
                    </div>
                    <div style={{ color: '#888' }}>
                      No hay {campo.tipo === 'IMAGEN' ? 'imagen' : 'archivo'} adjunto
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div key={campo.id} style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              {campo.nombre}
              {campo.esRequerido && <span style={{ color: '#6a5acd', marginLeft: '3px' }}>*</span>}
            </label>
            <div style={readOnlyStyle}>
              Tipo de campo no soportado: {campo.tipo}
            </div>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div style={{ fontSize: '20px', marginBottom: '10px' }}>Cargando historia clínica...</div>
        <div style={{
          width: '40px',
          height: '40px',
          border: '5px solid #f3f3f3',
          borderTop: '5px solid #6a5acd',
          borderRadius: '50%',
          margin: '0 auto',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Si no se encontró la historia clínica o hay un error
  if ((!historia && sinHistoria) || error) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '10px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '80px', color: '#e0e0e0', marginBottom: '20px' }}>
          <i className="fas fa-file-medical"></i>
        </div>
        <h3 style={{ marginBottom: '15px' }}>Historia clínica no encontrada</h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          {error || `No se pudo encontrar una historia clínica para el paciente con cédula: ${cedula}`}
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <Link to="/dashboard/pacientes" style={{
            display: 'inline-block',
            padding: '10px 20px',
            backgroundColor: '#6a5acd',
            color: 'white',
            borderRadius: '4px',
            textDecoration: 'none'
          }}>
            Ir a Pacientes
          </Link>
          <button
            onClick={crearHistoriaAutomatica}
            disabled={creandoHistoria}
            style={{
              padding: '10px 20px',
              backgroundColor: creandoHistoria ? '#9edce8' : '#40c8e0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: creandoHistoria ? 'wait' : 'pointer'
            }}
          >
            {creandoHistoria ? 'Creando historia...' : 'Crear historia clínica'}
          </button>
        </div>
      </div>
    );
  }

  const numeroHistoriaRaw = historia?.numero ?? paciente?.numero;
  const numeroHistoriaClinica = Number.isInteger(numeroHistoriaRaw)
    ? `HC-${String(numeroHistoriaRaw).padStart(4, '0')}`
    : 'Nueva';

  return (
    <div>
      {/* Información del paciente */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '10px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#6a5acd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              marginRight: '15px'
            }}>
              {paciente && paciente.nombre ? paciente.nombre.charAt(0) : '?'}
            </div>
            <div>
              <h2 style={{ margin: '0 0 5px 0' }}>
                {paciente ? `${paciente.nombre} ${paciente.apellido}` : 'Paciente'}
              </h2>
              <div style={{ color: '#666' }}>Cédula: {cedula}</div>
              {paciente && paciente.email && (
                <div style={{ color: '#666' }}>
                  {paciente.email} | {paciente.telefono || 'Sin teléfono'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <button
            onClick={async () => {
              try {
                // Fusionar los datos necesarios en el objeto historia
                const historiaCompleta = {
                  ...historia,
                  plantilla, // Incluir la estructura de secciones y campos
                  valores: valoresCampo // Incluir los valores de los campos
                };
                //Ver datos por consola
                const resultado = await generarYDescargarHistoriaClinica(
                  historiaCompleta,
                  paciente,
                  diagnosticos,
                  recetas,
                  currentUser?.nombre ? `Dr(a). ${currentUser.nombre}` : 'Dr(a). Sin especificar'
                );
                if (resultado) {
                  alert('Historia clínica descargada correctamente');
                } else {
                  alert('No se pudo generar la historia clínica');
                }
              } catch (error) {
                console.error('Error al generar historia clínica:', error);
              }
            }}
            style={buttonPrimaryStyle}
          >
            <i className="fas fa-download"></i>
            Descargar Historia
          </button>
          <div>
            <span style={{
              padding: '10px 15px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: 'bold',
              backgroundColor: '#f0f3fb',
              color: '#6a5acd'
            }}>
              Historia Clínica #{numeroHistoriaClinica}
            </span>
          </div>
        </div>
      </div>

      {/* Pestañas de navegación */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #ddd',
        marginBottom: '20px'
      }}>
        <div
          onClick={() => setPestanaActiva('datos')}
          style={{
            padding: '12px 20px',
            cursor: 'pointer',
            fontWeight: pestanaActiva === 'datos' ? 'bold' : 'normal',
            borderBottom: pestanaActiva === 'datos' ? '2px solid #6a5acd' : 'none',
            color: pestanaActiva === 'datos' ? '#6a5acd' : '#333'
          }}
        >
          <i className="fas fa-clipboard-list" style={{ marginRight: '8px' }}></i>
          Datos Clínicos
        </div>

        <div
          onClick={() => setPestanaActiva('diagnosticos')}
          style={{
            padding: '12px 20px',
            cursor: 'pointer',
            fontWeight: pestanaActiva === 'diagnosticos' ? 'bold' : 'normal',
            borderBottom: pestanaActiva === 'diagnosticos' ? '2px solid #6a5acd' : 'none',
            color: pestanaActiva === 'diagnosticos' ? '#6a5acd' : '#333'
          }}
        >
          <i className="fas fa-stethoscope" style={{ marginRight: '8px' }}></i>
          Diagnósticos
        </div>

        <div
          onClick={() => setPestanaActiva('recetas')}
          style={{
            padding: '12px 20px',
            cursor: 'pointer',
            fontWeight: pestanaActiva === 'recetas' ? 'bold' : 'normal',
            borderBottom: pestanaActiva === 'recetas' ? '2px solid #6a5acd' : 'none',
            color: pestanaActiva === 'recetas' ? '#6a5acd' : '#333'
          }}
        >
          <i className="fas fa-prescription" style={{ marginRight: '8px' }}></i>
          Recetas
        </div>
      </div>

      {/* Contenido según pestaña activa */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {/* Datos Clínicos */}
        {pestanaActiva === 'datos' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>
                Datos Clínicos - {plantilla?.nombre || 'Historia Clínica'}
              </h3>

              <button
                onClick={() => setModoEdicion(!modoEdicion)}
                style={modoEdicion ? buttonSecondaryStyle : buttonPrimaryStyle}
              >
                <i className={`fas fa-${modoEdicion ? 'times' : 'edit'}`}></i>
                {modoEdicion ? 'Cancelar Edición' : 'Editar Datos'}
              </button>
            </div>

            {/* Modo edición - Botón guardar */}
            {modoEdicion && (
              <div style={{
                backgroundColor: '#f0f9ff',
                padding: '15px',
                borderRadius: '6px',
                marginBottom: '25px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #d0e7ff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#0369a1' }}>
                  <i className="fas fa-info-circle" style={{ fontSize: '18px' }}></i>
                  <div>
                    <strong>Modo edición activado</strong> - Realice los cambios y guarde al finalizar.
                  </div>
                </div>
                <button
                  onClick={() => guardarCambios(false)}
                  disabled={guardando}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#40c8e0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: guardando ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <i className="fas fa-save"></i>
                  {guardando ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            )}

            {/* Secciones de la plantilla */}
            {plantilla?.secciones ? (
              <div>
                {plantilla.secciones
                  .sort((a, b) => a.orden - b.orden)
                  .map(seccion => (
                    <div key={seccion.id} style={{ marginBottom: '30px' }}>
                      <h4 style={{
                        borderBottom: '1px solid #eaeaea',
                        paddingBottom: '10px',
                        marginBottom: '20px',
                        color: '#444',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <i className="fas fa-clipboard-list" style={{ color: '#6a5acd' }}></i>
                        {seccion.nombre}
                      </h4>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                        {seccion.campos
                          .sort((a, b) => a.orden - b.orden)
                          .map(campo => renderizarCampo(campo, seccion))}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f3fb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 15px',
                  color: '#6a5acd',
                  fontSize: '36px'
                }}>
                  <i className="fas fa-clipboard"></i>
                </div>
                <p style={{ fontSize: '16px' }}>No hay datos clínicos disponibles o no se ha asignado una plantilla.</p>
              </div>
            )}
          </div>
        )}

        {/* —————————————————————————————————————————————— */}
        {/* ❸ Diagnósticos */}
        {pestanaActiva === 'diagnosticos' && (
          <div>
            {/* 1. Cabecera */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>Diagnósticos</h3>
              <button
                onClick={() => setMostrarModalDiagnostico(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6a5acd',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="fas fa-plus"></i> Agregar Diagnóstico
              </button>
            </div>

            {/* 2. Modal de Nuevo Diagnóstico */}
            {mostrarModalDiagnostico && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(5px)',
                animation: 'fadeIn 0.3s ease'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '25px',
                  borderRadius: '12px',
                  width: '500px',
                  maxWidth: '95%',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                  animation: 'slideIn 0.3s ease',
                  position: 'relative'
                }}>
                  {/* Botón de cierre */}
                  <button
                    onClick={() => setMostrarModalDiagnostico(false)}
                    style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      color: '#999',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      ':hover': { color: '#333', background: '#f5f5f5' }
                    }}
                  >
                    ×
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#6a5acd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px'
                    }}>
                      <i className="fas fa-stethoscope" style={{ color: 'white', fontSize: '16px' }}></i>
                    </div>
                    <h3 style={{ margin: 0, color: '#333', fontWeight: '600' }}>Nuevo Diagnóstico</h3>
                  </div>

                  <form onSubmit={guardarDiagnostico}>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#555'
                      }}>
                        Descripción <span style={{ color: '#e53e3e' }}>*</span>
                      </label>
                      <textarea
                        value={formDiagnostico.descripcion}
                        onChange={handleDiagnosticoChange}
                        name="descripcion"
                        required
                        placeholder="Describa el diagnóstico del paciente"
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          minHeight: '80px',
                          fontSize: '14px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          boxSizing: 'border-box',
                          resize: 'vertical',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#6a5acd',
                            boxShadow: '0 0 0 3px rgba(106, 90, 205, 0.1)'
                          }
                        }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          color: '#555'
                        }}>
                          Código CIE‑10
                        </label>
                        <input
                          type="text"
                          value={formDiagnostico.codigoCIE10}
                          onChange={handleDiagnosticoChange}
                          name="codigoCIE10"
                          placeholder="Ej: H40.1"
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            fontSize: '14px',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            boxSizing: 'border-box',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                            ':focus': {
                              outline: 'none',
                              borderColor: '#6a5acd',
                              boxShadow: '0 0 0 3px rgba(106, 90, 205, 0.1)'
                            }
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          color: '#555'
                        }}>
                          Fecha
                        </label>
                        <input
                          type="date"
                          value={formDiagnostico.fecha || ''}
                          onChange={handleDiagnosticoChange}
                          name="fecha"
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            fontSize: '14px',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            boxSizing: 'border-box',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                            ':focus': {
                              outline: 'none',
                              borderColor: '#6a5acd',
                              boxShadow: '0 0 0 3px rgba(106, 90, 205, 0.1)'
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#555'
                      }}>
                        Detalles
                      </label>
                      <textarea
                        value={formDiagnostico.detalles}
                        onChange={handleDiagnosticoChange}
                        name="detalles"
                        placeholder="Información adicional o notas sobre el diagnóstico"
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          minHeight: '60px',
                          fontSize: '14px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          boxSizing: 'border-box',
                          resize: 'vertical',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#6a5acd',
                            boxShadow: '0 0 0 3px rgba(106, 90, 205, 0.1)'
                          }
                        }}
                      />
                    </div>

                    <div style={{
                      marginTop: '25px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '12px',
                      borderTop: '1px solid #eee',
                      paddingTop: '20px'
                    }}>
                      <button
                        type="button"
                        onClick={() => setMostrarModalDiagnostico(false)}
                        style={{
                          padding: '10px 18px',
                          backgroundColor: '#f5f5f5',
                          color: '#444',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          ':hover': { backgroundColor: '#e5e5e5' }
                        }}
                      >
                        <i className="fas fa-times"></i> Cancelar
                      </button>
                      <button
                        type="submit"
                        style={{
                          padding: '10px 18px',
                          backgroundColor: '#6a5acd',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 4px rgba(106, 90, 205, 0.2)',
                          ':hover': { backgroundColor: '#5b4cbb' }
                        }}
                      >
                        <i className="fas fa-save"></i> Guardar Diagnóstico
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            <style jsx>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideIn {
                from { transform: translateY(-20px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
              }
            `}</style>

            {/* 3. Lista de Diagnósticos */}
            {diagnosticos.length > 0 ? (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ color: '#666', fontSize: '14px', margin: '0 0 15px 0' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '6px', color: '#3498db' }}></i>
                    Seleccione un diagnóstico para ver sus detalles o agregar una receta.
                  </p>
                </div>
                {diagnosticos.map(diag => (
                  <div
                    key={diag.id}
                    style={{
                      padding: '16px',
                      border: '1px solid #eaeaea',
                      borderRadius: '10px',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      backgroundColor: diagnosticoSeleccionado?.id === diag.id ? '#f8f4ff' : 'white',
                      boxShadow: diagnosticoSeleccionado?.id === diag.id
                        ? '0 2px 12px rgba(106, 90, 205, 0.15)'
                        : '0 1px 3px rgba(0,0,0,0.04)',
                      transition: 'all 0.2s ease',
                      borderLeft: diagnosticoSeleccionado?.id === diag.id
                        ? '4px solid #6a5acd'
                        : '1px solid #eaeaea',
                      ':hover': {
                        backgroundColor: diagnosticoSeleccionado?.id === diag.id ? '#f8f4ff' : '#fafafa',
                        boxShadow: '0 3px 10px rgba(0,0,0,0.08)'
                      }
                    }}
                    onClick={() => setDiagnosticoSeleccionado(diag)}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '5px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        color: '#666',
                        backgroundColor: '#f5f5f5',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <i className="far fa-calendar-alt"></i>
                        {new Date(diag.fecha).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>

                      {diag.codigoCIE10 && (
                        <div style={{
                          fontSize: '13px',
                          backgroundColor: '#e8f4fd',
                          color: '#3498db',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}>
                          CIE‑10: {diag.codigoCIE10}
                        </div>
                      )}
                    </div>

                    <h4 style={{
                      margin: '10px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#333',
                      lineHeight: '1.4'
                    }}>
                      {diag.descripcion}
                    </h4>

                    {diag.detalles && (
                      <div style={{
                        margin: '10px 0 0',
                        padding: '10px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#555',
                        lineHeight: '1.5'
                      }}>
                        <div style={{ fontWeight: '500', marginBottom: '4px', color: '#666', fontSize: '13px' }}>
                          Detalles adicionales:
                        </div>
                        {diag.detalles}
                      </div>
                    )}

                    {/* Indicador visual de selección */}
                    {diagnosticoSeleccionado?.id === diag.id && (
                      <div style={{
                        marginTop: '12px',
                        display: 'flex',
                        justifyContent: 'flex-end'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          color: '#6a5acd',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}>
                          <i className="fas fa-check-circle"></i>
                          Seleccionado
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                backgroundColor: '#f9f9fb',
                borderRadius: '10px',
                color: '#666',
                border: '1px dashed #ddd'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f0f7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 15px',
                  color: '#9ca3af',
                  fontSize: '24px'
                }}>
                  <i className="fas fa-stethoscope"></i>
                </div>
                <h4 style={{ margin: '0 0 10px 0', color: '#555' }}>No hay diagnósticos registrados</h4>
                <p style={{ margin: '0', fontSize: '14px' }}>Agregue un nuevo diagnóstico para el paciente</p>
                <button
                  onClick={() => setMostrarModalDiagnostico(true)}
                  style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: '#6a5acd',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-plus"></i>
                  Agregar diagnóstico
                </button>
              </div>
            )}
          </div>
        )}
        {/* —————————————————————————————————————————————— */}
        {/* ❹ Recetas Médicas */}
        {pestanaActiva === 'recetas' && (
          <div>
            {/* Cabecera */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>Recetas Médicas</h3>
              <button
                onClick={() => setMostrarModalReceta(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6a5acd',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="fas fa-plus"></i> Agregar Receta
              </button>
            </div>

            {/* Modal de Nueva Receta */}
            {mostrarModalReceta && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(5px)',
                animation: 'fadeIn 0.3s ease'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '25px',
                  borderRadius: '12px',
                  width: '600px',
                  maxWidth: '95%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                  animation: 'slideIn 0.3s ease',
                  position: 'relative'
                }}>
                  {/* Botón de cierre */}
                  <button
                    onClick={() => setMostrarModalReceta(false)}
                    style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      color: '#999',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      ':hover': { color: '#333', background: '#f5f5f5' }
                    }}
                  >
                    ×
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#40c8e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px'
                    }}>
                      <i className="fas fa-prescription" style={{ color: 'white', fontSize: '16px' }}></i>
                    </div>
                    <h3 style={{ margin: 0, color: '#333', fontWeight: '600' }}>Nueva Receta Médica</h3>
                  </div>

                  <form onSubmit={guardarReceta}>
                    {/* Seleccionar Diagnóstico */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#555'
                      }}>
                        Diagnóstico <span style={{ color: '#e53e3e' }}>*</span>
                      </label>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={formReceta.diagnosticoId}
                          onChange={e => setFormReceta({ ...formReceta, diagnosticoId: e.target.value })}
                          required
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            fontSize: '14px',
                            appearance: 'none',
                            backgroundColor: '#fff',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            boxSizing: 'border-box',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                            paddingRight: '30px',
                            ':focus': {
                              outline: 'none',
                              borderColor: '#40c8e0',
                              boxShadow: '0 0 0 3px rgba(64, 200, 224, 0.1)'
                            }
                          }}
                        >
                          <option value="" disabled>Seleccione un diagnóstico</option>
                          {diagnosticos.map(d => (
                            <option key={d.id} value={d.id}>
                              {new Date(d.fecha).toLocaleDateString()} — {d.descripcion.slice(0, 30)}{d.descripcion.length > 30 ? '...' : ''}
                            </option>
                          ))}
                        </select>
                        <div style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#888'
                        }}>
                          <i className="fas fa-chevron-down"></i>
                        </div>
                      </div>
                    </div>

                    {/* Indicaciones generales */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#555'
                      }}>
                        Indicaciones generales
                      </label>
                      <textarea
                        value={formReceta.indicaciones}
                        onChange={e => setFormReceta({ ...formReceta, indicaciones: e.target.value })}
                        placeholder="Instrucciones generales para todos los medicamentos"
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          minHeight: '80px',
                          fontSize: '14px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          boxSizing: 'border-box',
                          resize: 'vertical',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#40c8e0',
                            boxShadow: '0 0 0 3px rgba(64, 200, 224, 0.1)'
                          }
                        }}
                      />
                    </div>

                    {/* Sección de medicamentos */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px'
                      }}>
                        <h4 style={{ margin: 0, color: '#333', fontWeight: '600', fontSize: '16px' }}>
                          Medicamentos
                        </h4>
                        <button
                          type="button"
                          onClick={() => setFormReceta({
                            ...formReceta,
                            medicamentos: [...formReceta.medicamentos, { nombre: '', dosis: '', frecuencia: '', duracion: '', indicaciones: '' }]
                          })}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#f0f4ff',
                            color: '#4f46e5',
                            border: '1px solid #e0e7ff',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <i className="fas fa-plus"></i> Agregar medicamento
                        </button>
                      </div>

                      {/* Lista de medicamentos */}
                      {formReceta.medicamentos.map((med, idx) => (
                        <div key={idx} style={{
                          marginBottom: '15px',
                          padding: '16px',
                          border: '1px solid #e6e6e6',
                          borderRadius: '8px',
                          backgroundColor: '#fcfcfc',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          position: 'relative'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '15px',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '10px'
                          }}>
                            <h5 style={{ margin: 0, color: '#333', fontWeight: '600', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <i className="fas fa-pills" style={{ color: '#40c8e0' }}></i>
                              Medicamento #{idx + 1}
                            </h5>
                            {formReceta.medicamentos.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const nuevos = [...formReceta.medicamentos];
                                  nuevos.splice(idx, 1);
                                  setFormReceta({ ...formReceta, medicamentos: nuevos });
                                }}
                                style={{
                                  padding: '5px',
                                  backgroundColor: '#fff0f0',
                                  color: '#e53e3e',
                                  border: '1px solid #fee2e2',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px'
                                }}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            )}
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {/* Nombre del medicamento */}
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                fontSize: '13px',
                                color: '#555'
                              }}>
                                Nombre <span style={{ color: '#e53e3e' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={med.nombre}
                                onChange={e => {
                                  const nuevos = [...formReceta.medicamentos];
                                  nuevos[idx].nombre = e.target.value;
                                  setFormReceta({ ...formReceta, medicamentos: nuevos });
                                }}
                                placeholder="Ej: Paracetamol 500mg"
                                style={{
                                  width: '100%',
                                  maxWidth: '240px',
                                  padding: '10px',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  fontSize: '14px',
                                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                                }}
                                required
                              />
                            </div>

                            {/* Dosis */}
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                fontSize: '13px',
                                color: '#555'
                              }}>
                                Dosis
                              </label>
                              <input
                                type="text"
                                value={med.dosis}
                                onChange={e => {
                                  const nuevos = [...formReceta.medicamentos];
                                  nuevos[idx].dosis = e.target.value;
                                  setFormReceta({ ...formReceta, medicamentos: nuevos });
                                }}
                                placeholder="Ej: 1 tableta"
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  maxWidth: '240px',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  fontSize: '14px'
                                }}
                              />
                            </div>

                            {/* Frecuencia */}
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                fontSize: '13px',
                                color: '#555'
                              }}>
                                Frecuencia
                              </label>
                              <input
                                type="text"
                                value={med.frecuencia}
                                onChange={e => {
                                  const nuevos = [...formReceta.medicamentos];
                                  nuevos[idx].frecuencia = e.target.value;
                                  setFormReceta({ ...formReceta, medicamentos: nuevos });
                                }}
                                placeholder="Ej: Cada 8 horas"
                                style={{
                                  width: '100%',
                                  maxWidth: '240px',
                                  padding: '10px',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  fontSize: '14px'
                                }}
                              />
                            </div>

                            {/* Duración */}
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                fontSize: '13px',
                                color: '#555'
                              }}>
                                Duración
                              </label>
                              <input
                                type="text"
                                value={med.duracion}
                                onChange={e => {
                                  const nuevos = [...formReceta.medicamentos];
                                  nuevos[idx].duracion = e.target.value;
                                  setFormReceta({ ...formReceta, medicamentos: nuevos });
                                }}
                                placeholder="Ej: Por 7 días"
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  maxWidth: '240px',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  fontSize: '14px'
                                }}
                              />
                            </div>

                            {/* Indicaciones específicas */}
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                fontSize: '13px',
                                color: '#555'
                              }}>
                                Indicaciones específicas
                              </label>
                              <textarea
                                value={med.indicaciones}
                                onChange={e => {
                                  const nuevos = [...formReceta.medicamentos];
                                  nuevos[idx].indicaciones = e.target.value;
                                  setFormReceta({ ...formReceta, medicamentos: nuevos });
                                }}
                                placeholder="Instrucciones específicas para este medicamento"
                                style={{
                                  width: '100%',
                                  maxWidth: '95%',
                                  padding: '10px',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  minHeight: '60px',
                                  fontSize: '14px',
                                  resize: 'vertical'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Botones de acción */}
                    <div style={{
                      marginTop: '25px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '12px',
                      borderTop: '1px solid #eee',
                      paddingTop: '20px'
                    }}>
                      <button
                        type="button"
                        onClick={() => setMostrarModalReceta(false)}
                        style={{
                          padding: '10px 18px',
                          backgroundColor: '#f5f5f5',
                          color: '#444',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          ':hover': { backgroundColor: '#e5e5e5' }
                        }}
                      >
                        <i className="fas fa-times"></i> Cancelar
                      </button>
                      <button
                        type="submit"
                        style={{
                          padding: '10px 18px',
                          backgroundColor: '#40c8e0',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 4px rgba(64, 200, 224, 0.2)',
                          ':hover': { backgroundColor: '#36b6cc' }
                        }}
                      >
                        <i className="fas fa-save"></i> Guardar Receta
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Lista de Recetas */}
            {recetas.length > 0 ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h4 style={{ margin: 0, color: '#333', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-clipboard-list" style={{ color: '#40c8e0' }}></i>
                    Recetas del paciente
                  </h4>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                  {recetas.map(rec => (
                    <div key={rec.id} style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      border: '1px solid #f0f0f0',
                      transition: 'all 0.3s ease',
                      ':hover': { boxShadow: '0 6px 16px rgba(0,0,0,0.08)' }
                    }}>
                      <VisualizarReceta receta={rec} paciente={paciente} />
                      <div style={{
                        padding: '12px 16px',
                        borderTop: '1px solid #f0f0f0',
                        backgroundColor: '#fcfcfc',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          <i className="far fa-calendar-alt" style={{ marginRight: '5px' }}></i>
                          Creada el {new Date(rec.fecha || rec.createdAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <button
                          onClick={() => generarPDFReceta(
                            rec.medicamentos, paciente, rec.diagnostico, rec.proximaCita, `Dr(a). ${currentUser.nombre || ''}`
                          )}
                          style={{
                            padding: '8px 14px',
                            backgroundColor: '#6a5acd',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 4px rgba(106, 90, 205, 0.2)',
                            transition: 'all 0.2s ease',
                            ':hover': { backgroundColor: '#5b4cbb', transform: 'translateY(-1px)' }
                          }}
                        >
                          <i className="fas fa-print"></i> Imprimir receta
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                backgroundColor: '#f9f9fb',
                borderRadius: '10px',
                color: '#666',
                border: '1px dashed #ddd'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f0f7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 15px',
                  color: '#9ca3af',
                  fontSize: '24px'
                }}>
                  <i className="fas fa-prescription"></i>
                </div>
                <h4 style={{ margin: '0 0 10px 0', color: '#555' }}>No hay recetas registradas</h4>
                <p style={{ margin: '0', fontSize: '14px' }}>Agregue una nueva receta para el paciente</p>
                <button
                  onClick={() => {
                    if (diagnosticos.length > 0) {
                      setMostrarModalReceta(true);
                      // Preseleccionar el primer diagnóstico si no hay uno seleccionado
                      if (!formReceta.diagnosticoId && diagnosticos.length > 0) {
                        setFormReceta(prev => ({ ...prev, diagnosticoId: diagnosticos[0].id }));
                      }
                    } else {
                      alert('Debe agregar al menos un diagnóstico antes de crear una receta');
                    }
                  }}
                  style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: '#40c8e0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-plus"></i>
                  Agregar receta
                </button>
              </div>
            )}
          </div>
        )}
        {/* —————————————————————————————————————————————— */}

      </div>

      {/* Modal para ver imagen completa */}
      {
        mostrarImagenCompleta && imagenSeleccionada && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}>
            <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
              <img
                src={imagenSeleccionada}
                alt="Imagen ampliada"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  border: '2px solid white',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  console.error('Error al cargar imagen en modal:', imagenSeleccionada);
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/800x600?text=Error+al+cargar+imagen';
                }}
              />
              <button
                onClick={() => {
                  setMostrarImagenCompleta(false);
                  setImagenSeleccionada(null);
                }}
                style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  color: '#333',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                ×
              </button>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default HistoriaClinicaDetalle;